<script setup lang="ts">
import { useNotify } from "@/composition/notification";
import { useIMEComposition } from "@/composition/useIMEComposition";
import { useEmitter } from "@/composition/useEmitter";
import { useMethods } from "@/composition/useMethods";
import { getOtherOptionPlaceholderValue } from "@/composition/useOtherOption";
import { FormServices } from "@/service/FormServices";
import { LocationService } from "@/service/LocationServices";
import { PaymentServices } from "@/service/PaymentServices";
import { hasGroupAddress, PAYMENT_TAG } from "@/util/sellProductAddress";
import { SurveyServices } from "@/service/SurveyServices";
import { useAppStore } from "@/store/app";
import { useBuilderValidation } from "@/composition/useBuilderValidation";
import ErrorPopover from "@/components/common/Validation/ErrorPopover.vue";
import RecommendationsPanel from "@/components/common/Validation/RecommendationsPanel.vue";
import { elementKeyFor } from "@/validation";
import {
  apiMessages,
  toRuleResults,
} from "@/validation/customFieldErrors";
import type { CustomFieldFailure } from "@/validation/customFieldErrors";
import {
  sanitizeChoiceFieldCalculatedOptionsForSave,
  sanitizeSlideChoiceFieldCalculatedOptionsForSave,
} from "@/util/choiceFieldPayload";
import { MISMATCH_DATA_TYPE } from "@/util/constants";
import { isCfV1Enabled } from "@/util/customFieldsPhase1";
import { isCustomObjectField } from "@/util/customObjectUtils";
import {
  buildDateTimeValidation,
  isDateTimeField,
  stripDateTimeBuilderState,
} from "@/util/dateTimeSerialize";
import type { GroupField } from "@/util/methods";
import {
  convertSlideJumpToToConditionalLogic,
  convertSlideLogicToConditionalLogic,
  fixSafariCursor,
  generateFooterStyle,
  getAllFontsImport,
  getAllUniqueFonts,
  getCaretOffsetWithin,
  removeEmptyConditionalLogicRules,
  setCaretOffsetWithin,
} from "@/util/methods";
import {
  buildPictureChoiceCustomFieldPayload,
  getPictureChoiceElementKey,
  isPictureChoiceField,
  mergePictureChoiceCustomFieldResponse,
} from "@/util/pictureChoice";
import {
  normalizeUrlValidation,
} from "@/util/urlField";
import { isPostSavePanelHidden } from "@/util/userPreferences";
import {
  ArrowLeftIcon,
  Edit01Icon,
  EyeIcon,
  Save01Icon,
  Share07Icon,
} from "@gohighlevel/ghl-icons/24/outline";
import { UIButton, UIToolbar, UIToolbarGroup } from "@gohighlevel/ghl-ui";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

import { cloneDeep } from "lodash";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { createFieldCSS, defaultButtonStyle } from "../../util/default";
import { PRODUCT_TYPE as PaymentProductType } from "../../util/productConstants";
import AlertModal from "./AlertModal.vue";
import Integration from "./Integration/Integration.vue";
import PostSavePanel from "./Integrate/PostSavePanel.vue";

const { t } = useI18n();
const {
  runErrors,
  reportExternalErrors,
  recheckExternalErrors,
  revalidate,
  runRecommendations,
  recommendations,
  reset: resetValidation,
  isValidating,
  hasBlockingErrors,
  errorCount,
} = useBuilderValidation();

// Validation state is shared for the session, so clear it as this builder is
// set up. Findings from a previously opened asset would otherwise render Save
// disabled on the very first paint — and a disabled button never triggers the
// run that would clear them. Must happen before the first render, not in
// onMounted.
const showErrorPopover = ref(false);

/**
 * Reopens the error list. Rules broken since the last run are merged in first,
 * so asking to see the list always shows what is blocking the save right now.
 */
const openErrorPopover = (): void => {
  revalidate({ includeNew: true });
  // The header is its own stacking context, so the popover nested inside it can
  // never paint above the review panel however high its z-index goes. Two
  // overlapping surfaces would be confusing anyway, so the panel steps aside.
  showReviewPanel.value = false;
  showErrorPopover.value = true;
};

/**
 * Hover and click open the same list but dismiss differently: a list the
 * pointer opened follows the pointer away again, while one the user clicked
 * stays until they close it. The short delay bridges the gap between the Save
 * button and the popover so crossing it does not count as leaving.
 */
const HOVER_CLOSE_MS = 120;
const openedByHover = ref(false);
const hoverCloseTimer = ref<ReturnType<typeof setTimeout> | undefined>();

const cancelHoverClose = (): void => clearTimeout(hoverCloseTimer.value);

const openErrorPopoverOnHover = (): void => {
  cancelHoverClose();
  if (showErrorPopover.value) return;
  // Nothing to show, and opening anyway would be worse than doing nothing: the
  // review panel sits under this button, so reaching it means crossing here —
  // and opening this closes that.
  if (!errorCount.value) return;
  openedByHover.value = true;
  openErrorPopover();
};

const closeErrorPopoverOnLeave = (): void => {
  if (!openedByHover.value) return;
  cancelHoverClose();
  hoverCloseTimer.value = setTimeout(() => {
    showErrorPopover.value = false;
    openedByHover.value = false;
  }, HOVER_CLOSE_MS);
};

/** A click pins the list open, whether or not hover opened it first. */
const toggleErrorPopover = (): void => {
  cancelHoverClose();
  openedByHover.value = false;
  if (showErrorPopover.value) showErrorPopover.value = false;
  else openErrorPopover();
};

onBeforeUnmount(cancelHoverClose);

/** Runs validation and surfaces the list when it finds anything. */
const runErrorsAndSurface = (): number => {
  const found = runErrors().length;
  showErrorPopover.value = found > 0;
  return found;
};

const showReviewPanel = ref(false);

/**
 * Recomputes before showing: results are only as fresh as the last run, and the
 * user may have edited the builder since.
 */
const openReviewPanel = (): void => {
  runRecommendations();
  showReviewPanel.value = true;
};


/**
 * The pill count is only meaningful once recommendations have been computed,
 * which happens on save and whenever the panel opens — never continuously.
 */
const recommendationCount = computed(() => recommendations.value.length);
const { sendNotification } = useNotify();
const { sleep } = useMethods();
const emitter = useEmitter();

const store = useAppStore();

/**
 * The opt-out hides the pill as well as the save-time panel — a user who turned
 * recommendations off should not keep seeing a count for them.
 */
const recommendationsEnabled = computed(
  () => store.builderConfig.showRecommendationsBeforeSaving !== false
);


resetValidation();

/**
 * Cheap signature of the element structure: which elements exist and in what
 * order. Adding, removing or reordering changes it; editing a value does not.
 */
const structureSignature = computed(() => {
  const tags = store.formId
    ? (store.elements ?? []).map((element: GroupField) => element.tag)
    : (store.slides ?? []).flatMap((slide: { slideData?: GroupField[] }, index: number) =>
        (slide.slideData ?? []).map((element) => `${index}:${element.tag}`)
      );
  return tags.join(",");
});

// Findings address elements by position, so a structural edit has to re-map
// them. Without this a marker stays on the old index and labels whichever
// element moved into it.
watch(structureSignature, () => revalidate());

/**
 * Every element's label.
 *
 * Naming a field is what clears the API failure that asked for a name, but it
 * changes nothing structural — so without watching this the finding sits there
 * looking unfixed until the next save. Only the API findings are re-checked
 * here, not the whole registry, and only while there are any.
 */
const labelSignature = computed(() => {
  const labels = store.formId
    ? (store.elements ?? []).map((element: GroupField) => element.label)
    : (store.slides ?? []).flatMap((slide: { slideData?: GroupField[] }) =>
        (slide.slideData ?? []).map((element) => element.label)
      );
  return labels.join("\u0000");
});

watch(labelSignature, () => recheckExternalErrors());

/**
 * Keeps the Recommendations pill honest without waiting for a save.
 *
 * The pill is meant to be there from the moment the builder opens, so the count
 * cannot come from the save path alone. It is refreshed when the asset loads
 * and whenever its structure changes — not on every keystroke — and silently,
 * so these runs do not register as detections.
 */
const assetId = computed(
  () => store.formId || store.surveyId || store.quizId || ""
);

watch(
  // The results template is watched by reference: updateResultTemplate assigns
  // a new object, and without it a CTA fixed in the results builder leaves its
  // recommendation standing in a panel that is still open behind that modal.
  [assetId, structureSignature, () => store.resultTemplate],
  () => {
    if (!assetId.value) return;
    runRecommendations({ silent: true });
  },
  { immediate: true }
);
const router = useRouter();

const integrateModel = ref(false);
const scriptModalHiddenCheckbox = ref(false);
// "save" is the header Save button; only it may open the post-save panel.
// "integrate-tab" saves silently when the Integrate tab opens.
type SaveIntent = "save" | "preview" | "integrate" | "settings" | "integrate-tab";

const pendingScriptSaveIntent = ref<SaveIntent>("save");
const title = ref(t("common.alert"));
const description = ref(t("common.description"));
const positiveText = ref(t("common.ok"));
const negativeText = ref(t("common.cancel"));
const openAlertModal = ref(false);
const modalName = ref("");
const editHeader = ref(false);
const formNameInput = ref<any>(null);
const {
  handleCompositionStart,
  handleCompositionEnd,
  withIMECheck,
  isComposing,
} = useIMEComposition();

const isFormReady = computed(() => !!store.form);
// Blocking is owned by the validation registry: `url_field_domains_missing` and
// `footer_styles_invalid` are rules now, so neither greys the button out before
// the user has pressed Save and been told why.
const isSaveDisabled = computed(
  () =>
    !isFormReady.value ||
    store.isSaving ||
    isValidating.value ||
    hasBlockingErrors.value
);

// Date & Time validation payloads only ship for sub-accounts inside the rollout
// (allowlisted location or Labs flag on); everywhere else `dateTimeValidation`
// is stripped from every API payload.
const newDateTimeEnabled = computed(() => isCfV1Enabled(store.locationId));

const stripDateTimeStateFromFields = (
  fields: GroupField[] = []
): GroupField[] =>
  fields.map((field) =>
    stripDateTimeBuilderState(field, newDateTimeEnabled.value)
  );

type SlideWithFields = { slideData?: GroupField[] };

const stripDateTimeStateFromSlides = <T extends SlideWithFields>(
  slides: T[] = []
): T[] =>
  slides.map((slide) => ({
    ...slide,
    slideData: stripDateTimeStateFromFields(slide?.slideData ?? []),
  }));

// Handler method to call the imported fixSafariCursor function
const handleSafariCursor = (e: Event) => {
  const isKeyDown = e.type === "keydown";
  fixSafariCursor(e, isKeyDown);
};

const normalizedBaseUrl = computed(() => {
  const base: any = store.integrate && store.integrate.baseUrl;
  return Array.isArray(base) ? base[0] ?? "" : base || "";
});

watch(
  () => store.autoSaveFormOrSurvey,
  () => {
    // Route through the guarded save path so triggers (e.g. Notifications save)
    // still pass the HTML/script warning gate that the header Save button uses.
    void saveFormOrUpdate("settings");
  }
);

watch(
  () => store.triggerBack,
  (newValue) => {
    if (newValue) {
      back();
    }
  }
);

/**
 * Custom field failures from the current save.
 *
 * Collected rather than thrown so one bad field does not hide the others:
 * every request is allowed to settle, and the whole set is reported together
 * against the fields that caused it.
 */
const customFieldFailures = ref<CustomFieldFailure[]>([]);

/** A field's label without its markup, so a finding can name it plainly. */
const plainElementLabel = (element: { label?: unknown }): string =>
  typeof element?.label === "string"
    ? element.label.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()
    : "";

const fieldsMapping = async (formBuilderElements, slideIndex = undefined) => {
  const settled = await Promise.allSettled(
    formBuilderElements.map(async (element, elementIndex) => {
      const currentElement = element;
      const customField = element.custom
        ? store.customFields.find(
            (customField) => customField.id === element.id
          )
        : undefined;
      // Update the parentId of the element to update the parent folder of the custom field
      if (customField) {
        element.parentId = customField.parentId;
      }
      if (currentElement?.tag === "header") {
        delete currentElement.fontSize;
      }
      let customFieldParams: any = {
        ...(!element.customEdited || element.__pendingClone
          ? { name: element.customFieldLabel || element.label }
          : {}),
        fieldKey: element?.fieldKey,
        dataType: element?.dataType,
        documentType: "field",
        showInForms: true,
        model: element?.model === "opportunity" ? "opportunity" : "contact",
        parentId: element?.parentId || store.parentFolderId,
        description: "",
      };
      const isPictureChoice = isPictureChoiceField(element);

      if (isPictureChoice) {
        customFieldParams = {
          ...customFieldParams,
          ...buildPictureChoiceCustomFieldPayload(element),
        };
      }

      if (element.dataType === "TEXTBOX_LIST") {
        if (isCustomObjectField(element && element.model, ["business"])) {
          const normalized = (
            Array.isArray(element && element.picklistOptions)
              ? element.picklistOptions
              : []
          ).map((opt) => {
            if (typeof opt === "string") {
              return { label: opt, key: opt };
            }
            const label = (
              opt?.label ??
              opt?.key ??
              opt?.value ??
              ""
            ).toString();
            const key = (opt?.key ?? opt?.label ?? opt?.value ?? "").toString();
            return { label, key };
          });
          customFieldParams = {
            ...customFieldParams,
            options: normalized,
          };
        } else {
          const textBoxListOptions = (
            Array.isArray(element && element.picklistOptions)
              ? element.picklistOptions
              : []
          ).map((opt) => {
            if (typeof opt === "string") {
              return { label: opt, prefillValue: "" };
            }
            return {
              label: (opt?.label ?? opt?.key ?? opt?.value ?? "").toString(),
              prefillValue: (opt?.prefillValue ?? "").toString(),
            };
          });
          customFieldParams = {
            ...customFieldParams,
            textBoxListOptions,
          };
        }
      }
      if (
        !isPictureChoice &&
        ["SINGLE_OPTIONS", "MULTIPLE_OPTIONS", "CHECKBOX"].includes(
          element.dataType
        )
      ) {
        const isCustom = isCustomObjectField(element?.model, ["business"]);
        customFieldParams = {
          ...customFieldParams,
          options: Array.isArray(element?.picklistOptions)
            ? isCustom
              ? element.picklistOptions.map((option: any) => {
                  const label = (
                    typeof option === "string"
                      ? option
                      : option?.label ?? option?.key ?? option?.value ?? ""
                  ).toString();
                  return { label, key: label };
                })
              : (element.picklistOptions.map((option: any) =>
                  typeof option === "object" && option !== null
                    ? option.label
                    : option
                ) as string[])
            : element?.picklistOptions,
          ...(element.dataType === "CHECKBOX"
            ? {
                allowCustomOption: !!element?.allowCustomOption,
                ...(element?.allowCustomOption
                  ? {
                      placeholder: getOtherOptionPlaceholderValue(element, t),
                    }
                  : {}),
              }
            : {}),
        } as any;
      }
      if (element.dataType === "RADIO" && !isPictureChoice) {
        const imageOptions = Array.isArray(
          (element as any)?.picklistOptionsImage
        )
          ? ((element as any).picklistOptionsImage as any[])
          : [];
        const textOptions: string[] | undefined = Array.isArray(
          element?.picklistOptions
        )
          ? (element?.picklistOptions.map((option) =>
              typeof option === "object" && option !== null
                ? option.label
                : option
            ) as string[])
          : undefined;
        const hasImages = imageOptions.length > 0;
        customFieldParams = {
          ...customFieldParams,
          options: hasImages
            ? null
            : textOptions && textOptions.length
            ? textOptions
            : [],
          allowCustomOption: !!element?.allowCustomOption,
          ...(element?.allowCustomOption
            ? {
                placeholder: getOtherOptionPlaceholderValue(element, t),
              }
            : {}),
          ...(hasImages
            ? {
                picklistOptionsImage: imageOptions.map((opt: any) => ({
                  label: opt?.label,
                  src: opt?.src,
                })),
              }
            : {}),
        };
      }

      if (element.dataType === "FILE_UPLOAD") {
        customFieldParams = {
          ...customFieldParams,
          acceptedFormat:
            element?.picklistOptions?.length === 0
              ? ["all"]
              : element?.picklistOptions,
          isMultipleFile: element?.isMultipleFile,
          maxNumberOfFiles: element?.isMultipleFile
            ? parseInt(element?.maxFileSize)
            : undefined,
          // Access is chosen at creation and fixed afterwards; the update path
          // strips this again. Defaults to public, preserving old behaviour.
          isPrivate: element?.isPrivate === true,
        };
      }
      if (element.dataType === "SCORE") {
        customFieldParams = {
          ...customFieldParams,
          dataType: "NUMERICAL",
        };
      }
      if (element.dataType === "RATING") {
        customFieldParams = {
          ...customFieldParams,
          dataType: "NUMERICAL",
        };
      }
      if (element.dataType === "URL") {
        element.urlValidation = normalizeUrlValidation(
          store.surveyId && !element.customfieldUpdated
            ? customField?.urlValidation ?? element?.urlValidation
            : element?.urlValidation
        );
        customFieldParams = {
          ...customFieldParams,
          urlValidation: element.urlValidation,
        };
      }

      // Date & Time Picker: attach validation payload. Form-only metadata
      // (timeFormat/defaultTime/customTime) is intentionally NOT added here —
      // it lives on the form entity only (G7). `timezone` is server-added.
      if (isDateTimeField(element)) {
        currentElement.timeFormat = element.timeFormat ?? "24";
        currentElement.defaultTime = element.defaultTime ?? "none";
        customFieldParams = {
          ...customFieldParams,
          placeholder: element.placeholder,
          prefill: element.prefill ?? "",
          allowCustomOption: false,
        };
        // Validation payload only ships for allowlisted locations.
        if (newDateTimeEnabled.value) {
          const dateTimeValidation = buildDateTimeValidation(element);
          // Keep validation canonical on the form entity; hydrateable UI state
          // is stripped from the submitted builder JSON later.
          currentElement.dateTimeValidation = dateTimeValidation;
          customFieldParams = { ...customFieldParams, dateTimeValidation };
        }
      }

      try {
        let response;
        // Create once for new or pending cloned custom fields
        if (
          (element.custom && !element.customEdited) ||
          element.__pendingClone
        ) {
          response = await LocationService.createCustomField(
            store.locationId,
            customFieldParams
          );
        }
        // Only update existing custom fields; never update a pending clone.
        if (
          element.customfieldUpdated &&
          !element.__pendingClone &&
          !isCustomObjectField(element?.model, ["business"])
        ) {
          delete customFieldParams.fieldKey;
          // File access cannot be changed after the field is created.
          delete customFieldParams.isPrivate;
          response = await LocationService.updateCustomField(
            store.locationId,
            element?.Id || element?.id || element?._id,
            customFieldParams
          );
        }

        if (response?.status === 201 || response?.status === 200) {
          const apiField = response.data.customField || {};
          const base = {
            ...currentElement,
            ...apiField,
            customEdited: true,
            tag: apiField.id,
            fieldKey: apiField.fieldKey,
            hiddenFieldQueryKey:
              currentElement?.hiddenFieldQueryKey ||
              (apiField.fieldKey || "").split(".")[1],
            multipleFilesAllowed: currentElement?.isMultipleFile,
            multiFileLimit: currentElement?.isMultipleFile
              ? parseInt(currentElement?.maxFileSize)
              : undefined,
            placeholder: currentElement.placeholder,
            dataType: MISMATCH_DATA_TYPE[element.dataType]
              ? MISMATCH_DATA_TYPE[element.dataType]
              : apiField.dataType,
            description: currentElement.description,
            __pendingClone: false,
          } as any;

          if (base?.dataType === "URL") {
            base.urlValidation = normalizeUrlValidation(
              apiField?.urlValidation ?? currentElement?.urlValidation
            );
          }

          // on custom field update, remove the customfieldUpdated flag to avoid unnecessary API calls
          delete base.customfieldUpdated;

          if (isPictureChoice) {
            return mergePictureChoiceCustomFieldResponse(
              {
                ...base,
                picklistOptionsImage:
                  currentElement.picklistOptionsImage ??
                  base.picklistOptionsImage,
              },
              apiField
            );
          }

          // Normalize radio image vs text options so we don't keep both
          if (base?.dataType === "RADIO") {
            const imageOptionsRaw: any[] = Array.isArray(
              apiField?.picklistOptionsImage
            )
              ? (apiField.picklistOptionsImage as any[])
              : Array.isArray((apiField as any)?.picklistImageOptions)
              ? ((apiField as any).picklistImageOptions as any[])
              : Array.isArray(base?.picklistOptionsImage)
              ? (base.picklistOptionsImage as any[])
              : [];
            const imageOptions = imageOptionsRaw.map((opt: any) => ({
              label: opt?.label,
              src: opt?.src,
            }));
            const hasImages = imageOptions.length > 0;

            // Exclude text options when images exist
            const {
              picklistOptions: _omitPicklistOptions,
              picklistImageOptions: _omitAltImageOptions,
              ...rest
            } = base;

            return {
              ...rest,
              ...(hasImages
                ? { picklistOptionsImage: imageOptions }
                : {
                    picklistOptions: Array.isArray(base?.picklistOptions)
                      ? base.picklistOptions
                      : [],
                  }),
            };
          }
          return base;
        }
      } catch (error) {
        console.error(
          "Error in fieldsMapping for create and update custom fields",
          error
        );
        // Recorded against its field instead of thrown: the message alone
        // cannot say which field it came from once it leaves this scope.
        customFieldFailures.value.push({
          elementIndex,
          slideIndex,
          elementKey: elementKeyFor(element, elementIndex, slideIndex),
          label: plainElementLabel(element),
          messages: apiMessages(error),
        });
        return currentElement;
      }

      if (element.id !== undefined) {
        return currentElement;
      }
      delete currentElement.icon;
      delete currentElement.category;
      return currentElement;
    })
  );

  // An element whose mapping rejected outright keeps its previous value, so the
  // payload stays the shape the rest of the save expects.
  return settled.map((result, elementIndex) =>
    result.status === "fulfilled"
      ? result.value
      : formBuilderElements[elementIndex]
  );
};

const formSurveyBeforeSaveMapping = async () => {
  customFieldFailures.value = [];
  try {
    const fieldsOrSlides = store.formId ? store.elements : store.slides;
    const getRequiredFonts = getAllUniqueFonts(
      store.fieldStyle,
      store.submitMessageStyle,
      fieldsOrSlides,
      store.formId,
      store.footerStyle?.fontFamily,
      store.formAction.thankyouText,
      store.resultTemplate
    );
    if (validToCreateNewRecurringProduct()) {
      const product = createRecurringDonationProduct();
      const { data: productData } = await PaymentServices.createProduct(
        product
      );
      store.builderConfig.recurringProductId = productData._id;
      store.builderConfig.recurringProductCurrency =
        store.payment.currency.code;
      store.builderConfig.recurringProducts.push({
        currency: store.payment.currency.code,
        recurringProductId: productData._id,
      });
    } else {
      const recurringProduct = store.builderConfig?.recurringProducts?.find(
        (product) => product?.currency === store.payment?.currency?.code
      );
      if (recurringProduct) {
        store.builderConfig.recurringProductId =
          recurringProduct.recurringProductId;
        store.builderConfig.recurringProductCurrency =
          recurringProduct.currency;
      }
    }
    if (store.formId) {
      return formDataToBeSubmitted(getRequiredFonts);
    }
    if (store.surveyId) {
      return surveyDataToBeSubmitted(getRequiredFonts);
    }
    return quizDataToBeSubmitted(getRequiredFonts);
  } catch (error) {
    console.error("Error in formSurveyBeforeSaveMapping", error);
    throw error instanceof Error
      ? error
      : new Error("Failed to prepare the builder payload for save");
  }
};

const quizDataToBeSubmitted = async (getRequiredFonts) => {
  store.slides = await Promise.all(
    store.slides.map(async (slide, slideIndex) => ({
      ...slide,
      slideData: await fieldsMapping(slide.slideData, slideIndex),
    }))
  );
  const payloadSlides = stripDateTimeStateFromSlides(
    sanitizeSlideChoiceFieldCalculatedOptionsForSave(cloneDeep(store.slides))
  );
  store.footerStyle.computedStyles = generateFooterStyle(store, "desktop");
  store.mobileFooterStyle.computedStyles = generateFooterStyle(store, "mobile");
  const dataToBeSubmitted = {
    name: store.builder.name,
    formData: {
      fieldCSS:
        getAllFontsImport(
          getRequiredFonts,
          store.builderConfig.isGDPRCompliant
        ) +
          createFieldCSS(
            store.fieldStyle,
            store.builderConfig.inputStyleType
          ) || "",
      mobileFieldCSS:
        getAllFontsImport(
          getRequiredFonts,
          store.builderConfig.isGDPRCompliant
        ) +
          createFieldCSS(
            store.fieldStyle,
            store.builderConfig.inputStyleType,
            true
          ) || "",
      form: {
        company: {
          domain: store.company.domain,
          logoURL: store.company.logoURL,
          name: store.company.name,
        },
        customStyle: store.formStyle.customStyle || "",
        disableAutoNavigation: store.builderConfig.disableAutoNavigation,
        isBackButtonEnable: store.builderConfig.isBackButtonEnable,
        isSurveyScrollEnabled: store.builderConfig.isSurveyScrollEnabled,
        fbPixelId: store.facebook.fbPixelId || "",
        fieldStyle: store.fieldStyle,
        footerStyle: store.footerStyle,
        mobileFooterStyle: store.mobileFooterStyle,
        formAction: {
          actionType: store.formAction.actionType,
          redirect_url: store.formAction.redirectUrl,
          thankyouText: store.formAction.thankyouText,
          mobileThankyouText: (store.formAction as any).mobileThankyouText,
          footerHtml: store.formAction.footerHtml,
          headerFullWidthEnable: false,
          headerImageSrc: store.formAction.headerImageSrc,
          mobileHeaderImageSrc: store.formAction.mobileHeaderImageSrc,
          mobileHeaderImageSrcDeleted:
            store.formAction.mobileHeaderImageSrcDeleted ?? false,
        },
        showImage: store.builderConfig.showImage,
        desktopImageLayout: store.builderConfig.desktopImageLayout,
        mobileImageLayout: store.builderConfig.mobileImageLayout,
        imageFieldSpacing: store.builderConfig.imageFieldSpacing,
        imageCornerRadius: store.builderConfig.imageCornerRadius,
        imageFocusPoint: store.builderConfig.imageFocusPoint,
        surveyImageSettings: store.surveyImageSettings,
        isAnimationDisabled: store.builderConfig.isAnimationDisabled,
        formLabelVisible: store.builderConfig.formLabelVisible || false,
        formSubmissionEvent: store.facebook.formSubmissionEvent,
        inputStyleType: store.builderConfig.inputStyleType,
        pageViewEvent: store.facebook.pageViewEvent,
        stickyContact: store.builderConfig.stickyContact,
        // `generateSubmissionDocument` is deliberately omitted: for quizzes it is a
        // UI-only parent over the two checkboxes below, and the backend gates quiz
        // document upload on these two flags alone.
        generateQuizSubmissionDocument:
          store.builderConfig.generateQuizSubmissionDocument,
        generateQuizResultDocument:
          store.builderConfig.generateQuizResultDocument,
        showSubmissionInConversationsFeed:
          store.builderConfig.showSubmissionInConversationsFeed,
        isGDPRCompliant: store.builderConfig.isGDPRCompliant,
        showRecommendationsBeforeSaving:
          store.builderConfig.showRecommendationsBeforeSaving,
        enableTimezone: store.builderConfig.enableTimezone,
        formSchedule: store.builderConfig.formSchedule,
        enableSaveExitConfirmation:
          store.builderConfig.enableSaveExitConfirmation,
        // Conditional-logic editors can leave an empty draft row in state; it is
        // UI-only and must not become part of the persisted quiz contract.
        conditionalLogic: removeEmptyConditionalLogicRules(
          store.builderConfig.conditionalLogic
        ),
        currentThemeId: store.builderConfig.currentThemeId,
        style: {
          fieldSpacing: store.formStyle.fieldSpacing,
          ac_branding: store.formStyle.acBranding,
          background: store.formStyle.background,
          bgImage: store.formStyle.bgImage,
          mobileBgImage: store.formStyle.mobileBgImage,
          mobileBgImageDeleted: store.formStyle.mobileBgImageDeleted ?? false,
          border: store.formStyle.border,
          padding: store.formStyle.padding,
          mobilePadding: store.formStyle.mobilePadding,
          margin: store.formStyle.margin,
          mobileMargin: store.formStyle.mobileMargin,
          shadow: store.formStyle.shadow,
        },
        submitMessageStyle: store.submitMessageStyle,
        width: store.formStyle.width,
        emailNotificationsConfig: store.emailNotificationsConfig,
        opportunitySettings: store.opportunitySettings,
        contactAssociationSettings: store.contactAssociationSettings,
        autoResponderConfig: store.autoResponderConfig,
      },
      logic: store.logic,
      newFooter: store.builderConfig.newFooter,
      slides: payloadSlides,
      category: store.category,
      emailNotifications: store.builderConfig.emailNotifications,
      autoResponder: store.builderConfig.autoResponder,
      resultTemplate: store.resultTemplate,
      categoryCustomFields: stripDateTimeStateFromFields(
        sanitizeChoiceFieldCalculatedOptionsForSave(
          await fieldsMapping(store.categoryCustomFields)
        )
      ),
      parentFolderId: store.parentFolderId,
      parentFolderName: store.parentFolderName,
      language: localStorage.getItem("locale"),
    },
  };
  return dataToBeSubmitted;
};

const surveyDataToBeSubmitted = async (getRequiredFonts) => {
  store.slides = await Promise.all(
    store.slides.map(async (slide, slideIndex) => ({
      ...slide,
      slideData: await fieldsMapping(slide.slideData, slideIndex),
    }))
  );
  const slidesWithAddressFields = store.slides.map((slide) => {
    const slideData = [...(slide.slideData ?? [])];
    const groupAddressIndex = slideData.findIndex(
      (element) => element.tag === "group_address"
    );

    // if group address is present , add store.addressSettings.children after group_address
    if (groupAddressIndex !== -1) {
      const mappedAddressChildren = store?.addressSettings?.children ?? [];
      slideData.splice(groupAddressIndex + 1, 0, ...mappedAddressChildren);
    }
    return {
      ...slide,
      slideData,
    };
  });
  const clonedSlides = stripDateTimeStateFromSlides(
    sanitizeSlideChoiceFieldCalculatedOptionsForSave(slidesWithAddressFields)
  );
  store.sidebarOverlaySettings = { ...store.sidebarOverlaySettings };
  clonedSlides.map((value: any, key: any) => {
    value.button = {
      background: defaultButtonStyle.bgColor,
      color: defaultButtonStyle.color,
      border: {
        border: defaultButtonStyle.border,
        radius: defaultButtonStyle.radius,
        padding: defaultButtonStyle.padding,
      },
    };
  });
  if (store.builderConfig.newFooter) {
    store.footerStyle.computedStyles = generateFooterStyle(store, "desktop");
    store.mobileFooterStyle.computedStyles = generateFooterStyle(
      store,
      "mobile"
    );
  }

  const dataToBeSubmitted = {
    name: store.builder.name,
    formData: {
      fieldCSS:
        getAllFontsImport(
          getRequiredFonts,
          store.builderConfig.isGDPRCompliant
        ) +
          createFieldCSS(
            store.fieldStyle,
            store.builderConfig.inputStyleType
          ) || "",
      mobileFieldCSS:
        getAllFontsImport(
          getRequiredFonts,
          store.builderConfig.isGDPRCompliant
        ) +
          createFieldCSS(
            store.fieldStyle,
            store.builderConfig.inputStyleType,
            true
          ) || "",
      form: {
        company: {
          domain: store.company.domain,
          logoURL: store.company.logoURL,
          name: store.company.name,
        },
        customStyle: store.formStyle.customStyle || "",
        disableAutoNavigation: store.builderConfig.disableAutoNavigation,
        fbPixelId: store.facebook.fbPixelId || "",
        fieldStyle: store.fieldStyle,
        footerStyle: store.footerStyle,
        mobileFooterStyle: store.mobileFooterStyle,
        address: store.addressSettings,
        formAction: {
          actionType: store.formAction.actionType,
          disqualifiedText: store.formAction.disqualifiedText,
          disqualifiedType: store.formAction.disqualifiedType.toString(),
          disqualifiedUrl: store.formAction.disqualifiedUrl,
          endsurveyText: store.formAction.endsurveyText,
          endsurveyType: store.formAction.endsurveyType.toString(),
          endsurveyUrl: store.formAction.endsurveyUrl,
          fieldPerPage: store.formAction.fieldPerPage,
          fieldSettingEnable: store.formAction.fieldSettingEnable,
          footerHtml: store.formAction.footerHtml,
          headerFullWidthEnable: false,
          headerImageSrc: store.formAction.headerImageSrc,
          mobileHeaderImageSrc: store.formAction.mobileHeaderImageSrc,
          mobileHeaderImageSrcDeleted:
            store.formAction.mobileHeaderImageSrcDeleted ?? false,
          redirect_url: store.formAction.redirectUrl,
          thankyouText: store.formAction.thankyouText,
          mobileThankyouText: (store.formAction as any).mobileThankyouText,
        },
        showImage: store.builderConfig.showImage,
        desktopImageLayout: store.builderConfig.desktopImageLayout,
        mobileImageLayout: store.builderConfig.mobileImageLayout,
        imageFieldSpacing: store.builderConfig.imageFieldSpacing,
        imageCornerRadius: store.builderConfig.imageCornerRadius,
        imageFocusPoint: store.builderConfig.imageFocusPoint,
        surveyImageSettings: store.surveyImageSettings,
        formLabelVisible: store.builderConfig.formLabelVisible || false,
        formSubmissionEvent: store.facebook.formSubmissionEvent,
        isAnimationDisabled: store.builderConfig.isAnimationDisabled,
        isBackButtonEnable: store.builderConfig.isBackButtonEnable,
        isProgressBarEnabled: store.builderConfig.isProgressBarEnabled,
        isSurveyScrollEnabled: store.builderConfig.isSurveyScrollEnabled,
        inputStyleType: store.builderConfig.inputStyleType,
        pageViewEvent: store.facebook.pageViewEvent,
        stickyContact: store.builderConfig.stickyContact,
        generateSubmissionDocument:
          store.builderConfig.generateSubmissionDocument,
        showSubmissionInConversationsFeed:
          store.builderConfig.showSubmissionInConversationsFeed,
        isGDPRCompliant: store.builderConfig.isGDPRCompliant,
        showRecommendationsBeforeSaving:
          store.builderConfig.showRecommendationsBeforeSaving,
        enableTimezone: store.builderConfig.enableTimezone,
        formSchedule: store.builderConfig.formSchedule,
        enableSaveExitConfirmation:
          store.builderConfig.enableSaveExitConfirmation,
        // Persist only authored survey rules, not placeholder rows created while
        // opening or cancelling the conditional-logic editor.
        conditionalLogic: removeEmptyConditionalLogicRules(
          store.builderConfig.conditionalLogic
        ),
        currentThemeId: store.builderConfig.currentThemeId,
        style: {
          fieldSpacing: store.formStyle.fieldSpacing,
          ac_branding: store.formStyle.acBranding,
          background: store.formStyle.background,
          bgImage: store.formStyle.bgImage,
          mobileBgImage: store.formStyle.mobileBgImage,
          mobileBgImageDeleted: store.formStyle.mobileBgImageDeleted ?? false,
          border: store.formStyle.border,
          padding: store.formStyle.padding,
          mobilePadding: store.formStyle.mobilePadding,
          margin: store.formStyle.margin,
          mobileMargin: store.formStyle.mobileMargin,
          shadow: store.formStyle.shadow,
        },
        submitMessageStyle: store.submitMessageStyle,
        width: store.formStyle.width,
        emailNotificationsConfig: store.emailNotificationsConfig,
        opportunitySettings: store.opportunitySettings,
        contactAssociationSettings: store.contactAssociationSettings,
        autoResponderConfig: store.autoResponderConfig,
        payment: clonedSlides.filter(
          (slide) =>
            slide?.slideData?.filter((element) => element.tag === "payment")
              .length
        ).length
          ? paymentAndProductDetailsMap()
          : null,
      },
      slides: clonedSlides,
      surveyLogicLinkById: store.builderConfig.surveyLogicLinkById,
      newFooter: store.builderConfig.newFooter,
      enablePartialContactCreation:
        store.builderConfig.enablePartialContactCreation,
      emailNotifications: store.builderConfig.emailNotifications,
      autoResponder: store.builderConfig.autoResponder,
      parentFolderId: store.parentFolderId,
      parentFolderName: store.parentFolderName,
      recurringProductId: store.builderConfig.recurringProductId,
      recurringProductCurrency: store.builderConfig.recurringProductCurrency,
      recurringProducts: store.builderConfig.recurringProducts,
      language: localStorage.getItem("locale"),
    },
  };

  // Prepare conditional logic payload (merge slide.logic migration + slide.jumpTo)
  if (store.surveyId) {
    // Empty drafts must not make this list appear non-empty, otherwise they
    // would incorrectly suppress migration from legacy slide logic.
    const existing = removeEmptyConditionalLogicRules(
      store.builderConfig.conditionalLogic
    );
    let migrated = existing;
    if (!existing.length && store.builder.version >= 4) {
      const fromSlideLogic = convertSlideLogicToConditionalLogic(
        store.slides,
        store
      );
      migrated = Array.isArray(fromSlideLogic) ? fromSlideLogic : [];
    }
    const withJumpTo = convertSlideJumpToToConditionalLogic(
      store.slides,
      migrated
    );
    if (withJumpTo.length) {
      dataToBeSubmitted.formData.form.conditionalLogic = withJumpTo;
      store.builderConfig.conditionalLogic = withJumpTo;
    }
  }

  return dataToBeSubmitted;
};

const formDataToBeSubmitted = async (getRequiredFonts) => {
  const height = document?.getElementById(
    "form-builder-container"
  )?.offsetHeight;
  store.formStyle.height = height;
  store.elements = await fieldsMapping(store.elements);
  // adding to support the places where these address fields are used from this fields array
  const clonedFields = stripDateTimeStateFromFields(
    sanitizeChoiceFieldCalculatedOptionsForSave(cloneDeep(store.elements))
  );
  const groupAddressIndex = clonedFields.findIndex(
    (element) => element.tag === "group_address"
  );
  if (groupAddressIndex !== -1) {
    const mappedAddressChildren = store?.addressSettings?.children ?? [];
    clonedFields.splice(groupAddressIndex + 1, 0, ...mappedAddressChildren);
  }

  const dataToBeSubmitted = {
    name: store.builder.name,
    formData: {
      form: {
        button: store.builder.formData?.form?.button ? null : undefined,
        company: {
          domain: store.company.domain,
          logoURL: store.company.logoURL,
          name: store.company.name,
        },
        customStyle: store.formStyle.customStyle || "",
        fieldCSS:
          getAllFontsImport(
            getRequiredFonts,
            store.builderConfig.isGDPRCompliant
          ) +
            createFieldCSS(
              store.fieldStyle,
              store.builderConfig.inputStyleType
            ) || "",
        mobileFieldCSS:
          getAllFontsImport(
            getRequiredFonts,
            store.builderConfig.isGDPRCompliant
          ) +
            createFieldCSS(
              store.fieldStyle,
              store.builderConfig.inputStyleType,
              true
            ) || "",
        fbPixelId: store.facebook.fbPixelId || "",
        fieldStyle: store.fieldStyle,
        fields: clonedFields,
        payment: clonedFields.filter((element) => element.tag === "payment")
          .length
          ? paymentAndProductDetailsMap()
          : null,
        address: store.addressSettings,
        formAction: {
          actionType: store.formAction.actionType,
          redirect_url: store.formAction.redirectUrl,
          thankyouText: store.formAction.thankyouText,
          mobileThankyouText: (store.formAction as any).mobileThankyouText,
          headerImageSrc: store.formAction.headerImageSrc,
          mobileHeaderImageSrc: store.formAction.mobileHeaderImageSrc,
          mobileHeaderImageSrcDeleted:
            store.formAction.mobileHeaderImageSrcDeleted ?? false,
        },
        showImage: store.builderConfig.showImage,
        desktopImageLayout: store.builderConfig.desktopImageLayout,
        mobileImageLayout: store.builderConfig.mobileImageLayout,
        imageFieldSpacing: store.builderConfig.imageFieldSpacing,
        imageCornerRadius: store.builderConfig.imageCornerRadius,
        imageFocusPoint: store.builderConfig.imageFocusPoint,
        surveyImageSettings: store.surveyImageSettings,
        formLabelVisible: store.builderConfig.formLabelVisible || false,
        formSubmissionEvent: store.facebook.formSubmissionEvent,
        height: store.formStyle.height,
        layout: store.builderConfig.layout,
        mobileLayout: store.builderConfig.mobileLayout,
        inputStyleType: store.builderConfig.inputStyleType,
        pageViewEvent: store.facebook.pageViewEvent,
        stickyContact: store.builderConfig.stickyContact,
        generateSubmissionDocument:
          store.builderConfig.generateSubmissionDocument,
        showSubmissionInConversationsFeed:
          store.builderConfig.showSubmissionInConversationsFeed,
        isGDPRCompliant: store.builderConfig.isGDPRCompliant,
        showRecommendationsBeforeSaving:
          store.builderConfig.showRecommendationsBeforeSaving,
        enableTimezone: store.builderConfig.enableTimezone,
        formSchedule: store.builderConfig.formSchedule,
        enableSaveExitConfirmation:
          store.builderConfig.enableSaveExitConfirmation,
        // Form rules use the same draft-row lifecycle as surveys and quizzes,
        // so normalize them at the persistence boundary as well.
        conditionalLogic: removeEmptyConditionalLogicRules(
          store.builderConfig.conditionalLogic
        ),
        fullScreenMode: store.builderConfig.fullScreenMode,
        currentThemeId: store.builderConfig.currentThemeId,
        style: {
          fieldSpacing: store.formStyle.fieldSpacing,
          ac_branding: store.formStyle.acBranding,
          bgImage: store.formStyle.bgImage,
          mobileBgImage: store.formStyle.mobileBgImage,
          mobileBgImageDeleted: store.formStyle.mobileBgImageDeleted ?? false,
          background: store.formStyle.background,
          border: store.formStyle.border,
          padding: store.formStyle.padding,
          mobilePadding: store.formStyle.mobilePadding,
          margin: store.formStyle.margin,
          mobileMargin: store.formStyle.mobileMargin,
          shadow: store.formStyle.shadow,
        },
        submitMessageStyle: store.submitMessageStyle,
        width: store.formStyle.width,
        emailNotificationsConfig: store.emailNotificationsConfig,
        opportunitySettings: store.opportunitySettings,
        contactAssociationSettings: store.contactAssociationSettings,
        autoResponderConfig: store.autoResponderConfig,
      },
      recurringProductId: store.builderConfig.recurringProductId,
      recurringProductCurrency: store.builderConfig.recurringProductCurrency,
      recurringProducts: store.builderConfig.recurringProducts,
      emailNotifications: store.builderConfig.emailNotifications,
      autoResponder: store.builderConfig.autoResponder,
      parentFolderId: store.parentFolderId,
      parentFolderName: store.parentFolderName,
      language: localStorage.getItem("locale"),
    },
  };
  return dataToBeSubmitted;
};
const productPaymentFields = (): GroupField[] =>
  store.payment?.paymentType === "product"
    ? store.sellProductAddressSlides
        .flatMap((slide) => slide.slideData)
        .filter((field) => field?.tag === PAYMENT_TAG)
    : [];

/** Creates the location-level Billing Address custom fields before persisting a Billing-enabled payment. */
const provisionBillingAddressFields = async (): Promise<void> => {
  if (
    !hasGroupAddress(store.sellProductAddressSlides) ||
    !productPaymentFields().some((p) => p.billingAddressEnabled === true)
  ) {
    return;
  }
  const response = store.locationId
    ? await PaymentServices.enableBillingAddressFields(store.locationId).catch(
        () => undefined
      )
    : undefined;
  if (response?.data?.status !== true) {
    throw new Error(t("common.sellProductsAddress.billingAddressEnableFailed"));
  }
};

const continueAfterSave = (saved: boolean, intent: SaveIntent) => {
  if (!saved) return;
  if (intent === "preview") redirectToPreview();
  else if (intent === "integrate") integrateModel.value = true;
};

const formSaveAndUpdate = async (
  intent: SaveIntent = "save"
): Promise<boolean> => {
  if (runErrorsAndSurface()) {
    store.isSaving = false;
    store.saveSource = "";
    return false;
  }

  // Refreshes the pill's count. Saving no longer interrupts to show the panel —
  // that is the pill's job — but the count still has to come from somewhere, and
  // recommendations are computed on save and on panel open, never continuously.
  runRecommendations();

  try {
    store.isSaving = true;
    await provisionBillingAddressFields();
    const dataToBeSubmitted = await formSurveyBeforeSaveMapping();

    // The API decides what a valid custom field is, and only answers once the
    // save has tried. A field it rejected was never created, so persisting the
    // asset now would point it at something that does not exist.
    if (customFieldFailures.value.length) {
      reportExternalErrors(toRuleResults(customFieldFailures.value));
      showErrorPopover.value = true;
      store.isSaving = false;
      store.saveSource = "";
      return false;
    }
    // Convert any hex color values in payload to brandboard keys before saving
    store.convertFormDataColorsToKeys(dataToBeSubmitted);
    store.anyUnsavedChanges = false;
    if (store.formId || store.quizId) {
      const builderId = store.formId || store.quizId;
      const { data: formData } = await FormServices.updateFormById(
        builderId,
        dataToBeSubmitted
      );
      store.builder.versionHistory = formData.data.versionHistory;
      if (store.quizId) {
        store.setCategoryCustomFields(
          formData.data?.formData?.categoryCustomFields
        );
      }
    } else {
      const { data: surveyData } = await SurveyServices.updateFormById(
        store.surveyId,
        dataToBeSubmitted
      );
      store.builder.versionHistory = surveyData.data.versionHistory;
    }
    await sleep(2000);
    store.isSaving = false;
    emitter.emit("builder:saved", { widgetId: store.integrateWidget.id });
    notifySaveResult(true);
    if (
      intent === "save" &&
      store.isIntegrateTabEnabled &&
      !isPostSavePanelHidden()
    ) {
      store.showPostSavePanel = true;
    }
    store.emitBrandColorUsage();
    store.saveSource = "";
    // Post-save refresh is non-critical: the save already succeeded, so a refresh
    // failure must not flip the reported result to an error.
    try {
      await store.getCustomFieldFolders();
      await store.getCustomFields();
    } catch (refreshError) {
      console.error("Failed to refresh custom fields after save", refreshError);
    }
    return true;
  } catch (err: unknown) {
    store.anyUnsavedChanges = true;
    store.isSaving = false;
    notifySaveResult(false, err instanceof Error ? err.message : undefined);
    store.saveSource = "";
    return false;
  }
};

/**
 * Scoped save feedback. When a save was initiated from the Notifications view
 * (`store.saveSource` set), show a settings-specific success/error toast.
 * The header Save button keeps its existing behaviour: silent on success,
 * generic error toast on failure.
 */
const notifySaveResult = (success: boolean, errorMessage?: string) => {
  if (success) {
    if (!store.saveSource) {
      return;
    }
    sendNotification({
      title: t("common.savedSuccessfully"),
      description:
        store.saveSource === "autoResponder"
          ? t("common.autoResponderSettingsUpdated")
          : t("common.emailNotificationSettingsUpdated"),
      type: "success",
    });
    return;
  }
  sendNotification({
    title: store.saveSource
      ? t("common.couldntSaveChanges")
      : t("common.errorRetryAgain"),
    description: errorMessage,
    type: "error",
  });
};

const createRecurringDonationProduct = () => {
  return {
    name: `${store.builder.name} - ${t("common.recurringDonation")}`,
    locationId: store.locationId,
    productType: "DIGITAL",
    availableInStore: true,
    seo: {
      title: "",
      description: "",
    },
    medias: [],
    variants: [],
    isTaxesEnabled: false,
    isLabelEnabled: false,
    taxInclusive: false,
    prices: [
      {
        name: `${store.builder.name} - ${t("common.recurringDonationDay")}`,
        type: "recurring",
        currency: store.payment.currency.code,
        amount: 1,
        recurring: {
          interval: "day",
          intervalCount: 1,
        },
      },
      {
        name: `${store.builder.name} - ${t("common.recurringDonationWeek")}`,
        type: "recurring",
        currency: store.payment.currency.code,
        amount: 1,
        recurring: {
          interval: "week",
          intervalCount: 1,
        },
      },
      {
        name: `${store.builder.name} - ${t("common.recurringDonationMonth")}`,
        type: "recurring",
        currency: store.payment.currency.code,
        amount: 1,
        recurring: {
          interval: "month",
          intervalCount: 1,
        },
      },
      {
        name: `${store.builder.name} - ${t("common.recurringDonationYear")}`,
        type: "recurring",
        currency: store.payment.currency.code,
        amount: 1,
        recurring: {
          interval: "year",
          intervalCount: 1,
        },
      },
    ],
    meta: {
      internalSource: "recurring_donation",
    },
  };
};

const validToCreateNewRecurringProduct = () => {
  return (
    (store.payment?.recurringDonation?.isRecurringDonationEnabled &&
      !store.builderConfig.recurringProductId) ||
    (store.payment?.recurringDonation?.isRecurringDonationEnabled &&
      !store.builderConfig.recurringProducts?.some(
        (product) => product.currency === store.payment.currency.code
      ))
  );
};
const back = () => {
  if (store.anyUnsavedChanges) {
    setupAlertModalForBack();
  } else if (store.isModal && store.appHandshake) {
    const id = store.formId || store.surveyId || store.quizId;
    store.appHandshake.then((parent) =>
      parent.emit("modalClose", { id, name: store.builder.name })
    );
  } else {
    router.go(-1);
  }
};

const openPreview = async () => {
  if (runErrorsAndSurface()) return;

  if (
    containHTML() &&
    !JSON.parse(localStorage.getItem("script_modal_hidden"))
  ) {
    pendingScriptSaveIntent.value = "preview";
    await setupAlertModalForScript("preview");
  } else if (store.anyUnsavedChanges) {
    continueAfterSave(await formSaveAndUpdate("preview"), "preview");
  } else {
    redirectToPreview();
  }
};

const redirectToPreview = () => {
  if (store.formId) {
    window
      .open(
        `${store.previewDomain}/widget/form/${store.formId}?notrack=true`,
        "_blank"
      )
      .focus();
  } else if (store.surveyId) {
    window
      .open(
        `${store.previewDomain}/widget/survey/${store.surveyId}?notrack=true`,
        "_blank"
      )
      .focus();
  } else {
    window
      .open(
        `${store.previewDomain}/widget/quiz/${store.quizId}?notrack=true`,
        "_blank"
      )
      .focus();
  }
};

const goBackToMain = () => {
  if (store.isModal && store.appHandshake) {
    const id = store.formId || store.surveyId || store.quizId;
    store.appHandshake.then((parent) =>
      parent.emit("modalClose", { id, name: store.builder.name })
    );
  } else {
    router.go(-1);
  }
};

const saveBeforeIntegrate = async () => {
  if (!store.isIntegrateTabEnabled) return;
  if (!store.anyUnsavedChanges) return;
  await saveFormOrUpdate("integrate-tab");
};

const openIntegrateFormAndSave = async () => {
  // Checked before the modal opens, the way Preview does it: opening first
  // would cover the error popover and leave the blocked save unexplained.
  if (runErrorsAndSurface()) return;

  if (containHTML()) {
    pendingScriptSaveIntent.value = "integrate";
    await setupAlertModalForScript("integrate");
  } else if (store.anyUnsavedChanges) {
    continueAfterSave(await formSaveAndUpdate("integrate"), "integrate");
  } else {
    integrateModel.value = true;
  }
};

const updateCheckBox = (isChecked: boolean) => {
  scriptModalHiddenCheckbox.value = isChecked;
};

const feedback = async (modalName, result) => {
  localStorage.setItem(
    "script_modal_hidden",
    scriptModalHiddenCheckbox.value.toString()
  );
  if (modalName === "BACK" && result) {
    goBackToMain();
  }
  if (modalName === "SCRIPT") {
    if (result) {
      const intent = pendingScriptSaveIntent.value;
      continueAfterSave(await formSaveAndUpdate(intent), intent);
    } else {
      // Save cancelled at the script warning — clear any pending notification
      // save source so it can't leak into the next save.
      store.saveSource = "";
    }
  }
  openAlertModal.value = false;
};

const containHTML = () => {
  if (store.formId) {
    return store.elements.some((element) => element.tag === "html");
  }
  let containAnyHTMLInSurvey = false;
  store.slides.forEach((slide) => {
    if (slide.slideData.some((element) => element.tag === "html")) {
      containAnyHTMLInSurvey = true;
    }
  });
  return containAnyHTMLInSurvey;
};

const integrationAction = (result) => {
  integrateModel.value = result;
};

const popModalChange = () => {
  openAlertModal.value = false;
  // Modal dismissed without saving — clear any pending notification save source.
  store.saveSource = "";
};

const saveFormOrUpdate = async (
  intent: SaveIntent = "save"
): Promise<boolean> => {
  if (containHTML()) {
    pendingScriptSaveIntent.value = intent;
    return setupAlertModalForScript(intent);
  }
  return formSaveAndUpdate(intent);
};

const setupAlertModalForScript = async (
  intent: SaveIntent = "save"
): Promise<boolean> => {
  pendingScriptSaveIntent.value = intent;
  title.value = `${t("common.alert")}!`;
  description.value = store.formId
    ? `${t("form.thirdPartyScriptAlertMessage")}`
    : `${t("survey.thirdPartyScriptAlertMessage")}`;
  positiveText.value = `${t("common.proceed")}`;
  negativeText.value = `${t("common.cancel")}`;
  modalName.value = "SCRIPT";
  openAlertModal.value = !JSON.parse(
    localStorage.getItem("script_modal_hidden")
  );
  if (!openAlertModal.value) {
    const saved = await formSaveAndUpdate(intent);
    continueAfterSave(saved, intent);
    return saved;
  }
  return false;
};
const setupAlertModalForBack = () => {
  modalName.value = "BACK";
  title.value = `${t("common.alert")}!`;
  description.value = store.formId
    ? `${t("form.unsavedAlertMessage")}`
    : `${t("survey.unsavedAlertMessage")}`;
  positiveText.value = `${t("common.proceed")}`;
  negativeText.value = `${t("common.cancel")}`;
  openAlertModal.value = true;
};
const updateName = (e: Event) => {
  const target = e.target as HTMLElement;

  const text = (target.textContent || "").replace(/\u200B/g, "");

  store.builder.name = text;
  store.anyUnsavedChanges = true;
};

onMounted(() => {
  emitter.on("integrate-tab-opened", saveBeforeIntegrate);
  if (formNameInput.value) {
    formNameInput.value.textContent = store.builder.name ?? "";
  }
});

onBeforeUnmount(() => {
  emitter.off("integrate-tab-opened", saveBeforeIntegrate);
});

// Keep DOM synced when store changes from elsewhere (autosave, API, etc.)
watch(
  () => store.builder.name,
  async (v) => {
    if (isComposing.value) {
      return;
    }
    await nextTick();
    const el = formNameInput.value as HTMLElement | null;
    if (!el) {
      return;
    }
    if (el.textContent !== (v ?? "")) {
      el.textContent = v ?? "";
    }
  }
);

const compositionEndHandler = handleCompositionEnd(updateName);
const handleInputChange = withIMECheck(updateName);

const handleEnterPress = (e: KeyboardEvent) => {
  if (e.key === "Enter" && formNameInput.value) {
    formNameInput.value?.blur();
  }
};

const paymentAndProductDetailsMap = () => {
  const products = store.addedProductList.map((product) => {
    return {
      productId: product.productId,
      showDescription: product.showDescription,
      showImage: product.showImage,
      showQuantity: product.showQuantity,
      selectedPriceIds: product.selectedPriceIds,
    };
  });

  const bumpProducts = store.bumProductList.map((product) => {
    return {
      productId: product.productId,
      priceId: product.priceId,
      headline: product.headline || "",
      otoHeadline: product.otoHeadline || "",
      otoText: product.otoText || "",
      enableImage: product.enableImage || false,
      uploadedImage: product.uploadedImage,
      type: PaymentProductType.BUMP_PRODUCT.value,
    };
  });
  store.payment.products = products;
  store.payment.bumpProducts = bumpProducts;
  return store.payment;
};
</script>
<template>
  <div id="hl-builder-header">
    <UIToolbar class="header-toolbar p-0">
      <UIToolbarGroup class="min-w-[360px] flex-1 basis-0">
        <div class="flex">
          <div v-if="!store.isModal" class="border-right p-3">
            <div class="flex pr-3">
              <UIButton
                id="builder-back"
                text
                size="small"
                class="text-gray-800"
                @click="back"
              >
                <ArrowLeftIcon class="mr-2 h-4 w-4" />
                <span>{{ t("common.back") }}</span>
              </UIButton>
            </div>
          </div>
        </div>
      </UIToolbarGroup>
      <UIToolbarGroup>
        <div
          class="builder-form-name flex items-center justify-center text-black"
        >
          <div
            ref="formNameInput"
            :contenteditable="true"
            role="textbox"
            tabindex="0"
            :aria-label="t('form.formName')"
            :class="`min-w-[1rem] max-w-2xl text-lg outline-none ${
              !editHeader ? 'truncate' : ''
            }`"
            @compositionstart="handleCompositionStart"
            @compositionend="compositionEndHandler"
            @input="handleInputChange"
            @click="editHeader = true"
            @focusout="
              editHeader = false;
              store.addToHistory();
            "
            @keydown="handleEnterPress"
          ></div>
          <Edit01Icon
            v-if="!editHeader"
            class="ml-1 h-4 w-4 text-gray-700 hover:text-gray-500"
            @click="
              editHeader = true;
              formNameInput.focus();
            "
          />
        </div>
      </UIToolbarGroup>

      <UIToolbarGroup class="min-w-[360px] flex-1 basis-0">
        <div class="flex items-center justify-end px-3 py-2">
          <!-- <div
            v-if="store.quizId"
            class="preview-border border-1 flex items-center justify-center rounded-lg p-[10px] px-2"
            @click="openPreview"
          >
            <EyeIcon class="h-4 w-4"></EyeIcon>
          </div> -->
          <UIButton
            id="integrate-btn"
            size="small"
            class="mr-2 text-gray-800"
            :disabled="!isFormReady"
            @click="openPreview"
          >
            <EyeIcon class="h-4 w-4"></EyeIcon>
            <div class="ml-2">{{ t("common.preview") }}</div>
          </UIButton>
          <UIButton
            v-if="!store.isModal && !store.isIntegrateTabEnabled"
            id="integrate-btn"
            data-testid="legacy-integrate-button"
            size="small"
            class="mr-2 text-gray-800"
            :disabled="!isFormReady"
            @click="openIntegrateFormAndSave"
          >
            <Share07Icon class="h-4 w-4"></Share07Icon>
            <div class="ml-2">{{ t("common.integrate") }}</div>
          </UIButton>
          <button
            v-if="recommendationsEnabled && !errorCount"
            id="builder-recommendations-pill"
            type="button"
            class="builder-recommendations-pill-btn mr-2 inline-flex items-center gap-2 rounded-md bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700"
            @click="openReviewPanel"
          >
            {{ t("builderValidation.ui.recommendationsPill") }}
            <!-- The count is dropped at zero rather than the whole pill: the
                 panel also holds Insights, which has nothing to do with how
                 many recommendations there are and would otherwise be
                 unreachable on a clean asset. -->
            <span
              v-if="recommendationCount"
              class="rounded-full bg-blue-100 px-1.5 text-xs leading-5 text-blue-700"
            >
              {{ recommendationCount }}
            </span>
          </button>
          <div
            class="relative"
            @mouseenter="openErrorPopoverOnHover"
            @mouseleave="closeErrorPopoverOnLeave"
            @focusin="openErrorPopoverOnHover"
            @focusout="closeErrorPopoverOnLeave"
          >
          <UIButton
            id="save-action"
            size="small"
            type="primary"
            :disabled="isSaveDisabled"
            @click="saveFormOrUpdate('save')"
          >
            <Save01Icon class="mr-2 h-4 w-4"></Save01Icon>
            <span v-if="isValidating">{{
              t("builderValidation.ui.checking")
            }}</span>
            <span v-else-if="!store.isSaving">{{ t("common.save") }}</span>
            <span v-else>{{ t("common.saving") }}</span>
            <!-- Unresolved blocking errors from the last run. Takes precedence
                 over the unsaved-changes dot: a blocked save is the more
                 actionable of the two. -->
            <span
              v-if="errorCount"
              id="builder-save-error-count"
              class="absolute right-0 top-0 z-10 -mr-1.5 -mt-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold leading-none text-white"
            >
              {{ errorCount }}
            </span>
            <span
              v-else-if="store.anyUnsavedChanges"
              class="absolute right-0 top-0 z-10 -mr-1 -mt-1 flex h-3 w-3"
            >
              <span
                class="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-70"
              ></span>
              <span
                class="relative inline-flex h-3 w-3 rounded-full bg-amber-400"
              ></span>
            </span>
          </UIButton>
          <!-- Save is disabled while errors stand, so the badge carries the
               click that reopens the list. -->
          <!-- Only while Save is actually disabled. A disabled button swallows
               its own clicks, so this carries them; over a usable button it
               would intercept the press and the save would never happen. -->
          <button
            v-if="errorCount && isSaveDisabled"
            id="builder-save-error-trigger"
            type="button"
            class="builder-save-error-trigger absolute inset-0 cursor-pointer"
            :aria-label="t('builderValidation.ui.viewErrors')"
            @focus="openErrorPopoverOnHover"
            @blur="closeErrorPopoverOnLeave"
            @click="toggleErrorPopover"
          ></button>
          <ErrorPopover
            :show="showErrorPopover"
            @pointer-enter="cancelHoverClose"
            @pointer-leave="closeErrorPopoverOnLeave"
            @update:show="showErrorPopover = $event"
          />
          <RecommendationsPanel
            :show="showReviewPanel"
            @update:show="showReviewPanel = $event"
          />
          </div>
        </div>
      </UIToolbarGroup>
    </UIToolbar>
    <AlertModal
      v-if="openAlertModal"
      :title="title"
      :description="description"
      :modal-name="modalName"
      :show-modal-popup="openAlertModal"
      :positive-text="positiveText"
      :negative-text="negativeText"
      :is-checkbox-enabled="true"
      :hide-modal="scriptModalHiddenCheckbox"
      header-type="warning"
      footer-type="primary"
      @pop:change="popModalChange"
      @pop:positive="feedback"
      @pop:negative="feedback"
      @pop:check="updateCheckBox"
    ></AlertModal>
    <Integration
      v-if="integrateModel"
      :model-active="integrateModel"
      :form-style="store.formStyle"
      :form-name="store.builder.name"
      :base-url="String(store.integrate.baseUrl || '')"
      :is-white-labelled="store.integrate.isWhiteLabelled"
      :form-layout-settings="store.integrate.formLayoutSettings"
      @update:show-model="integrationAction"
    >
    </Integration>
    <PostSavePanel v-if="store.isIntegrateTabEnabled && store.showPostSavePanel" />
  </div>
</template>

<style scoped>
.header-toolbar {
  background-color: #ffffff !important;
  border-bottom: 0.5px solid #e2e2e2 !important;
}

/* Transparent hit area over Save: it must paint nothing of its own or it
   covers the button underneath. */
.builder-save-error-trigger {
  background: none;
  border: none;
  padding: 0;
}
/* Bare button, so it needs its border cleared like the rest. */
.builder-recommendations-pill-btn {
  border: none;
}
.builder-form-name {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-align: center;
}

#hl-builder-header .hl-toolbar-group {
  padding: 5px 0px;
}

.form-save-btn {
  width: 100%;
}

#hl-builder-header {
  position: fixed;
  z-index: 21;
  width: 100%;
}

.border-right {
  border-right: 1px solid #dee2e6 !important;
}

.hl-toolbar-group {
  padding: 0px !important;
}

.preview-border {
  border: 1 solid #eff4ff;
  background-color: #eff4ff;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
}
.result-button {
  border-radius: 8px;
  border: 1px solid #d0d5dd;
  background: #fff;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
}
</style>
